import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getTerminals } from "../../data/terminals";
import { Terminal } from "../../types/Terminal";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  FilterAlt as FilterAltIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Map as MapIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import TerminalForm from "../Forms/Terminal";
import TerminalMapModal from "./TerminalMap";

const TERMINAL_FORM_MOUNT_DELAY_MS = 150;

const COUNTRY_OPTIONS = [
  { value: "Belgium", flag: "🇧🇪", translationKey: "countryBelgium" },
  { value: "Netherlands", flag: "🇳🇱", translationKey: "countryNetherlands" },
  { value: "Germany", flag: "🇩🇪", translationKey: "countryGermany" },
  { value: "France", flag: "🇫🇷", translationKey: "countryFrance" },
];

type TerminalDialogState = {
  title: string;
  mode: "create" | "edit";
  terminal: Terminal | null;
  modalKey: string;
};

const normalizeText = (value?: string | null) => {
  return value?.toString().trim().toLowerCase() ?? "";
};

const getNormalizedCountry = (country?: string | null) => {
  if (!country) {
    return "";
  }

  const normalizedCountry = country.trim().toLowerCase();

  switch (normalizedCountry) {
    case "belgium":
    case "belgie":
    case "belgië":
    case "belgique":
    case "belgien":
    case "be":
      return "Belgium";

    case "netherlands":
    case "nederland":
    case "pays-bas":
    case "paysbas":
    case "niederlande":
    case "nl":
      return "Netherlands";

    case "germany":
    case "duitsland":
    case "allemagne":
    case "deutschland":
    case "de":
      return "Germany";

    case "france":
    case "frankrijk":
    case "frankreich":
    case "fr":
      return "France";

    default:
      return country;
  }
};

const getCountryConfig = (country?: string | null) => {
  const normalizedCountry = getNormalizedCountry(country);

  return COUNTRY_OPTIONS.find((item) => item.value === normalizedCountry);
};

const getCountryFlag = (country?: string | null) => {
  return getCountryConfig(country)?.flag ?? "";
};

const getCountryTranslationKey = (country?: string | null) => {
  return getCountryConfig(country)?.translationKey;
};

const formatAliases = (aliases?: string[] | null) => {
  if (!Array.isArray(aliases) || aliases.length === 0) {
    return "";
  }

  return aliases.join(", ");
};

const renderColorIndicator = (color?: string | null) => {
  if (!color) {
    return null;
  }

  return (
    <Box
      title={color}
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        backgroundColor: color,
        border: "1px solid rgba(0, 0, 0, 0.2)",
        display: "inline-block",
        verticalAlign: "middle",
      }}
    />
  );
};

const getTerminalIdentity = (terminal: Terminal, index: number) => {
  return (
    (terminal as any)["@id"] ??
    terminal.id ??
    `${terminal.abbreviation ?? "terminal"}-${terminal.name ?? "unknown"}-${index}`
  ).toString();
};

const getTerminalRowKey = (terminal: Terminal, index: number) => {
  return getTerminalIdentity(terminal, index);
};

const TerminalListDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const terminals = useSelector((state: any) => state.terminals);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedCity, setSelectedCity] = useState("all");

  const [terminalDialog, setTerminalDialog] =
    useState<TerminalDialogState | null>(null);
  const [terminalDialogReady, setTerminalDialogReady] = useState(false);
  const [mapTerminal, setMapTerminal] = useState<Terminal | null>(null);

  const refreshTerminals = useCallback(() => {
    dispatch(getTerminals() as any);
  }, [dispatch]);

  useEffect(() => {
    refreshTerminals();
  }, [refreshTerminals]);

  useEffect(() => {
    const handleTerminalSaved = () => {
      refreshTerminals();
    };

    window.addEventListener("terminal-crud:saved", handleTerminalSaved);

    return () => {
      window.removeEventListener("terminal-crud:saved", handleTerminalSaved);
    };
  }, [refreshTerminals]);

  useEffect(() => {
    if (!terminalDialog) {
      setTerminalDialogReady(false);
      return;
    }

    setTerminalDialogReady(false);

    const timeoutId = window.setTimeout(() => {
      setTerminalDialogReady(true);
    }, TERMINAL_FORM_MOUNT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [terminalDialog]);

  const terminalData: Terminal[] = useMemo(() => {
    const rawData: Terminal[] = Array.isArray(terminals?.data)
      ? terminals.data
      : [];

    const seen = new Set<string>();

    return rawData.filter((terminal: Terminal, index: number) => {
      const key = getTerminalIdentity(terminal, index);

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [terminals?.data]);

  const countryOptions = useMemo(() => {
    return terminalData
      .map((terminal: any) => getNormalizedCountry(terminal.country))
      .filter(Boolean)
      .filter((value: string, index: number, array: string[]) => {
        return array.findIndex((item) => item === value) === index;
      })
      .sort((a: string, b: string) => {
        const aKey = getCountryTranslationKey(a);
        const bKey = getCountryTranslationKey(b);

        return (aKey ? t(aKey) : a).localeCompare(bKey ? t(bKey) : b);
      });
  }, [terminalData, t]);

  const cityOptions = useMemo(() => {
    return terminalData
      .filter((terminal: any) => {
        if (selectedCountry === "all") {
          return true;
        }

        return getNormalizedCountry(terminal.country) === selectedCountry;
      })
      .map((terminal: any) => terminal.city)
      .filter(Boolean)
      .filter((value: string, index: number, array: string[]) => {
        return array.findIndex((item) => item === value) === index;
      })
      .sort((a: string, b: string) => a.localeCompare(b));
  }, [terminalData, selectedCountry]);

  const filteredTerminals = useMemo(() => {
    const normalizedSearchTerm = normalizeText(searchTerm);

    return terminalData.filter((terminal: Terminal) => {
      const normalizedCountry = getNormalizedCountry((terminal as any).country);
      const countryTranslationKey = getCountryTranslationKey(normalizedCountry);
      const translatedCountry = countryTranslationKey
        ? t(countryTranslationKey)
        : normalizedCountry;

      const terminalName = normalizeText(terminal.name);
      const abbreviation = normalizeText(terminal.abbreviation);
      const city = normalizeText(terminal.city);
      const country = normalizeText(normalizedCountry);
      const localizedCountry = normalizeText(translatedCountry);

      const aliases = Array.isArray((terminal as any).aliases)
        ? (terminal as any).aliases
            .map((alias: string) => normalizeText(alias))
            .join(" ")
        : "";

      const matchesSearch =
        !normalizedSearchTerm ||
        terminalName.includes(normalizedSearchTerm) ||
        abbreviation.includes(normalizedSearchTerm) ||
        aliases.includes(normalizedSearchTerm) ||
        city.includes(normalizedSearchTerm) ||
        country.includes(normalizedSearchTerm) ||
        localizedCountry.includes(normalizedSearchTerm);

      const matchesCountry =
        selectedCountry === "all" || normalizedCountry === selectedCountry;

      const matchesCity =
        selectedCity === "all" || terminal.city === selectedCity;

      return matchesSearch && matchesCountry && matchesCity;
    });
  }, [terminalData, searchTerm, selectedCountry, selectedCity, t]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCountry("all");
    setSelectedCity("all");
  };

  const closeTerminalDialog = () => {
    setTerminalDialog(null);
    setTerminalDialogReady(false);
  };

  const openAddTerminalModal = () => {
    setTerminalDialog({
      title: t("addTerminal"),
      mode: "create",
      terminal: null,
      modalKey: `terminal-create-${Date.now()}`,
    });
  };

  const openEditTerminalModal = (terminal: Terminal) => {
    setTerminalDialog({
      title: t("editTerminal"),
      mode: "edit",
      terminal: {
        ...terminal,
        country: getNormalizedCountry((terminal as any).country),
      },
      modalKey:
        (terminal as any)["@id"] ??
        terminal.id?.toString() ??
        `terminal-edit-${Date.now()}`,
    });
  };

  const openTerminalMapModal = (terminal: Terminal) => {
    setMapTerminal(terminal);
  };

  const closeTerminalMapModal = () => {
    setMapTerminal(null);
  };

  return (
    <Box>
      <Dialog
        open={Boolean(terminalDialog)}
        onClose={closeTerminalDialog}
        maxWidth="lg"
        fullWidth
        keepMounted={false}
        PaperProps={{
          sx: {
            width: "95vw",
            maxWidth: 1400,
            maxHeight: "95vh",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              {terminalDialog?.title ?? ""}
            </Typography>

            <IconButton type="button" onClick={closeTerminalDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {!terminalDialogReady || !terminalDialog ? (
            <Box sx={{ py: 6, px: 4 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mb: 2 }}
              >
                {t("loading") || "Loading..."}
              </Typography>
              <LinearProgress />
            </Box>
          ) : (
            <TerminalForm
              key={terminalDialog.modalKey}
              terminal={terminalDialog.terminal}
              mode={terminalDialog.mode}
              onClose={closeTerminalDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(mapTerminal)}
        onClose={closeTerminalMapModal}
        maxWidth="lg"
        fullWidth
        keepMounted={false}
        PaperProps={{
          sx: {
            width: "95vw",
            maxWidth: 1200,
            maxHeight: "95vh",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              {t("showMap")}
            </Typography>

            <IconButton type="button" onClick={closeTerminalMapModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {mapTerminal ? <TerminalMapModal terminal={mapTerminal} /> : null}
        </DialogContent>
      </Dialog>

      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        sx={{
          mb: 2,
        }}
      >
        <Grid item xs={12} md={3.2}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              height: 20,
              lineHeight: "20px",
              visibility: "hidden",
            }}
          >
            {t("searchTerminal")}
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t("searchTerminal")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm ? (
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() => setSearchTerm("")}
                      edge="end"
                      title={t("clearSearch")}
                      aria-label={t("clearSearch")}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <SearchIcon fontSize="small" color="action" />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              height: 20,
              lineHeight: "20px",
            }}
          >
            {t("country")}
          </Typography>

          <FormControl
            fullWidth
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
              },
            }}
          >
            <Select
              value={selectedCountry}
              onChange={(event) => {
                setSelectedCountry(event.target.value);
                setSelectedCity("all");
              }}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{
                sx: {
                  zIndex: 99999,
                },
                PaperProps: {
                  sx: {
                    zIndex: 100000,
                    maxHeight: 320,
                  },
                },
              }}
            >
              <MenuItem value="all">{t("allCountries")}</MenuItem>

              {countryOptions.map((country: string) => {
                const translationKey = getCountryTranslationKey(country);

                return (
                  <MenuItem key={country} value={country}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>{getCountryFlag(country)}</span>
                      <span>{translationKey ? t(translationKey) : country}</span>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              height: 20,
              lineHeight: "20px",
            }}
          >
            {t("city")}
          </Typography>

          <FormControl
            fullWidth
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
              },
            }}
          >
            <Select
              value={selectedCity}
              onChange={(event) => setSelectedCity(event.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{
                sx: {
                  zIndex: 99999,
                },
                PaperProps: {
                  sx: {
                    zIndex: 100000,
                    maxHeight: 320,
                  },
                },
              }}
            >
              <MenuItem value="all">{t("allCities")}</MenuItem>

              {cityOptions.map((city: string) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={1.6}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              height: 20,
              lineHeight: "20px",
              visibility: "hidden",
            }}
          >
            {t("clearFilters")}
          </Typography>

          <Button
            type="button"
            fullWidth
            variant="outlined"
            size="small"
            onClick={clearFilters}
            startIcon={<FilterAltIcon />}
            sx={{
              height: 40,
              whiteSpace: "nowrap",
            }}
          >
            {t("clearFilters")}
          </Button>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              height: 20,
              lineHeight: "20px",
              visibility: "hidden",
            }}
          >
            {t("addTerminal")}
          </Typography>

          <Button
            type="button"
            fullWidth
            variant="contained"
            size="small"
            onClick={openAddTerminalModal}
            startIcon={<AddIcon />}
            sx={{
              height: 40,
              whiteSpace: "nowrap",
            }}
          >
            {t("addTerminal")}
          </Button>
        </Grid>
      </Grid>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          minHeight: "50vh",
          maxHeight: "70vh",
          overflow: "auto",
          borderRadius: 2,
        }}
      >
        <Table stickyHeader size="small" aria-label={t("terminal")}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("abbreviation")}
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>{t("name")}</TableCell>

              <TableCell align="center" sx={{ fontWeight: 700 }}>
                {t("aliases")}
              </TableCell>

              <TableCell align="center" sx={{ fontWeight: 700 }}>
                {t("city")}
              </TableCell>

              <TableCell align="center" sx={{ fontWeight: 700 }}>
                {t("country")}
              </TableCell>

              <TableCell align="center" sx={{ fontWeight: 700 }}>
                {t("color")}
              </TableCell>

              <TableCell align="center" sx={{ fontWeight: 700 }}>
                {t("location")}
              </TableCell>

              <TableCell align="center" sx={{ fontWeight: 700 }}>
                {t("actions")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredTerminals.map((terminal: Terminal, index: number) => {
              const normalizedCountry = getNormalizedCountry(
                (terminal as any).country
              );
              const countryTranslationKey =
                getCountryTranslationKey(normalizedCountry);

              return (
                <TableRow
                  key={getTerminalRowKey(terminal, index)}
                  hover
                  sx={{
                    "& td": {
                      py: 1.25,
                      color: "text.secondary",
                    },
                  }}
                >
                  <TableCell>{terminal.abbreviation ?? ""}</TableCell>

                  <TableCell>{terminal.name ?? ""}</TableCell>

                  <TableCell align="center">
                    {formatAliases((terminal as any).aliases)}
                  </TableCell>

                  <TableCell align="center">{terminal.city ?? ""}</TableCell>

                  <TableCell align="center">
                    {normalizedCountry ? (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span>{getCountryFlag(normalizedCountry)}</span>
                        <span>
                          {countryTranslationKey
                            ? t(countryTranslationKey)
                            : normalizedCountry}
                        </span>
                      </Box>
                    ) : (
                      ""
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {renderColorIndicator(terminal.color)}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      type="button"
                      size="small"
                      title={t("showMap")}
                      aria-label={t("showMap")}
                      onClick={() => openTerminalMapModal(terminal)}
                    >
                      <MapIcon fontSize="small" />
                    </IconButton>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      type="button"
                      size="small"
                      title={t("editTerminal")}
                      aria-label={t("editTerminal")}
                      onClick={() => openEditTerminalModal(terminal)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredTerminals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t("noMatches")}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TerminalListDialog;